import { onTrackerReady } from './trackers.ts';

// E-commerce events for every tag a shop can configure:
//  - GTM: a GA4-style `{ event, ecommerce }` object on dataLayer (tags in the container trigger on it),
//  - gtag.js (GA4 and/or Google Ads set in Integracje → Google): `gtag('event', ...)` — gtag.js ignores
//    the GTM-style object above, it only handles gtag() commands. Without send_to an event reaches every
//    configured destination, so Ads gets view_item/add_to_cart/... for remarketing audiences too,
//  - Meta Pixel: the matching standard event.
// Tags load lazily and only after full cookie consent (SiteAnalytics.tsx), so gtag/fbq calls go
// through onTrackerReady and run once the tag has initialised.

export async function trackEvent(eventName: 'add_to_cart' | 'initiate_checkout') {
  if (typeof window === 'undefined') return;
  try {
    await fetch('/api/track-event', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event_name: eventName,
      }),
    });
  } catch (error) {
    console.error(`Failed to register traffic analytics event ${eventName}:`, error);
  }
}

export interface EcommerceItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  item_category?: string;
  item_variant?: string;
  item_brand?: string;
  index?: number;
  item_list_id?: string;
  item_list_name?: string;
}

/**
 * The id the product feeds use as g:id (backend shop/logic/seo.py): the SKU, else the product id;
 * a variant uses its own SKU, else "<product id>-<variant id>". Google Ads dynamic remarketing and
 * conversions with cart data match event items to feed items by this value.
 */
export function feedItemId(product: any, variant?: any): string {
  const base = String(product?.sku || product?.id || '');
  if (variant && variant.id) return String(variant.sku || `${base}-${variant.id}`);
  return base;
}

/** Feed id of a cart line — cart/checkout enrich lines with `feed_id`; older carts fall back to sku/id. */
export function cartItemFeedId(item: any): string {
  return String(item?.feed_id || item?.sku || item?.id || '');
}

export function lastCategoryName(product: any): string {
  const path = product?.category_path;
  if (Array.isArray(path) && path.length > 0) return path[path.length - 1]?.name || '';
  const cats = product?.categories;
  if (Array.isArray(cats) && cats.length > 0) return cats[cats.length - 1]?.name || '';
  return '';
}

export function cartLineToItem(item: any): EcommerceItem {
  return {
    item_id: cartItemFeedId(item),
    item_name: item?.name || '',
    price: Number(item?.price) || 0,
    quantity: Number(item?.quantity) || 1,
    item_category: item?.category_name || '',
    ...(item?.attributes && Object.keys(item.attributes).length > 0
      ? { item_variant: Object.values(item.attributes).join(', ') }
      : {}),
  };
}

function hasTrackingConsent(): boolean {
  try {
    return localStorage.getItem('cookie-consent') === 'all';
  } catch {
    return false;
  }
}

function itemsValue(items: EcommerceItem[]): number {
  return Math.round(items.reduce((sum, i) => sum + i.price * i.quantity, 0) * 100) / 100;
}

/** GA4 reads item_id; Google Ads dynamic remarketing reads id + google_business_vertical. */
function toGtagItems(items: EcommerceItem[]) {
  return items.map((i) => ({ ...i, id: i.item_id, google_business_vertical: 'retail' }));
}

function pushDataLayer(payload: Record<string, any>) {
  try {
    window.dataLayer = window.dataLayer || [];
    // Clear the previous ecommerce object first, as Google recommends — GTM merges dataLayer state.
    if ('ecommerce' in payload) window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(payload);
  } catch (e) {
    console.error(`Failed to push ${payload.event} to dataLayer:`, e);
  }
}

function sendGtag(eventName: string, params: Record<string, any>) {
  onTrackerReady('gtag', () => {
    try {
      window.gtag?.('event', eventName, params);
    } catch (e) {
      console.error(`Failed to send ${eventName} via gtag:`, e);
    }
  });
}

function sendPixel(eventName: string, params: Record<string, any>, options?: Record<string, any>) {
  onTrackerReady('fbq', () => {
    try {
      if (options) window.fbq?.('track', eventName, params, options);
      else window.fbq?.('track', eventName, params);
    } catch (e) {
      console.error(`Failed to track ${eventName} on Meta Pixel:`, e);
    }
  });
}

function sendEcommerce(
  eventName: string,
  currency: string,
  items: EcommerceItem[],
  extra: Record<string, any> = {},
  pixel?: { name: string; params?: Record<string, any> },
) {
  if (typeof window === 'undefined' || !hasTrackingConsent()) return;
  const value = extra.value ?? itemsValue(items);
  const ecommerce = { currency, value, ...extra, items };
  pushDataLayer({ event: eventName, ecommerce });
  sendGtag(eventName, { ...ecommerce, items: toGtagItems(items) });
  if (pixel) {
    sendPixel(pixel.name, {
      content_ids: items.map((i) => i.item_id),
      content_type: 'product',
      value,
      currency,
      ...(pixel.params || {}),
    });
  }
}

function productPrice(product: any, variant?: any): number {
  const source = variant || product;
  const sale = source?.sales_price;
  return Number(sale !== null && sale !== undefined && sale > 0 ? sale : source?.price) || 0;
}

function productToItem(product: any, variant?: any, quantity: number = 1): EcommerceItem {
  const variantName = variant?.attributes ? Object.values(variant.attributes).join(', ') : '';
  return {
    item_id: feedItemId(product, variant),
    item_name: product?.name || '',
    price: productPrice(product, variant),
    quantity,
    item_category: lastCategoryName(product),
    ...(product?.brand?.name ? { item_brand: product.brand.name } : {}),
    ...(variantName ? { item_variant: variantName } : {}),
  };
}

export function trackViewItem(product: any, currency: string = 'PLN') {
  if (!product) return;
  const item = productToItem(product);
  sendEcommerce('view_item', currency, [item], {}, {
    name: 'ViewContent',
    params: { content_name: item.item_name, content_category: item.item_category || undefined },
  });
}

export function trackViewItemList(products: any[], listId: string, listName: string, currency: string = 'PLN') {
  if (!products || products.length === 0) return;
  // GA4 accepts up to 200 items; the first screenfuls are what was actually seen.
  const items = products.slice(0, 30).map((p, index) => ({
    ...productToItem(p),
    index,
    item_list_id: listId,
    item_list_name: listName,
  }));
  sendEcommerce('view_item_list', currency, items, { item_list_id: listId, item_list_name: listName });
}

/** A sent contact/inquiry form — the conversion a website (no shop) has. Pixel: Lead. */
export function trackLead(formType: string = 'contact') {
  if (typeof window === 'undefined' || !hasTrackingConsent()) return;
  pushDataLayer({ event: 'generate_lead', form_type: formType });
  sendGtag('generate_lead', { form_type: formType });
  sendPixel('Lead', { content_name: formType });
}

export function trackSearch(term: string) {
  const searchTerm = term.trim();
  if (typeof window === 'undefined' || !searchTerm || !hasTrackingConsent()) return;
  pushDataLayer({ event: 'search', search_term: searchTerm });
  sendGtag('search', { search_term: searchTerm });
  sendPixel('Search', { search_string: searchTerm });
}

export function trackAddToCart(
  product: any,
  variant?: any,
  quantity: number = 1,
  currency: string = 'PLN',
  options?: {
    customId?: string;
    customName?: string;
    customPrice?: number;
  }
) {
  if (typeof window === 'undefined') return;

  // Track internally in DB
  trackEvent('add_to_cart');

  const item = productToItem(product, variant, quantity);
  if (options?.customId) item.item_id = options.customId;
  if (options?.customName) item.item_name = options.customName;
  if (options?.customPrice !== undefined) item.price = options.customPrice;
  if (variant?.attributes && !options?.customName) {
    item.item_name = `${item.item_name} (${Object.values(variant.attributes).join(', ')})`;
  }

  sendEcommerce('add_to_cart', currency, [item], {}, {
    name: 'AddToCart',
    params: { content_name: item.item_name },
  });
}

export function trackAddToCartBulk(items: EcommerceItem[], currency: string = 'PLN') {
  if (typeof window === 'undefined') return;

  trackEvent('add_to_cart');

  sendEcommerce('add_to_cart', currency, items.map((i) => ({ ...i, item_category: i.item_category || '' })), {}, {
    name: 'AddToCart',
    params: { content_name: items.map((i) => i.item_name).join(', ') },
  });
}

export function trackRemoveFromCart(cartLines: any[], currency: string = 'PLN') {
  if (!cartLines || cartLines.length === 0) return;
  sendEcommerce('remove_from_cart', currency, cartLines.map(cartLineToItem));
}

export function trackViewCart(cartLines: Array<any>, currency: string = 'PLN') {
  if (!cartLines || cartLines.length === 0) return;
  sendEcommerce('view_cart', currency, cartLines.map(cartLineToItem));
}

export function trackBeginCheckout(cartLines: Array<any>, currency: string = 'PLN') {
  if (!cartLines || cartLines.length === 0) return;
  const items = cartLines.map(cartLineToItem);
  sendEcommerce('begin_checkout', currency, items, {}, {
    name: 'InitiateCheckout',
    params: { num_items: items.reduce((sum, i) => sum + i.quantity, 0) },
  });
}

/** One-page checkout: shipping and payment are both "submitted" when the order form is sent. */
export function trackCheckoutSubmitted(
  cartLines: Array<any>,
  currency: string,
  shippingTier: string | null,
  paymentType: string | null,
  coupon?: string | null,
) {
  if (!cartLines || cartLines.length === 0) return;
  const items = cartLines.map(cartLineToItem);
  const extra = coupon ? { coupon } : {};
  if (shippingTier) sendEcommerce('add_shipping_info', currency, items, { ...extra, shipping_tier: shippingTier });
  if (paymentType) {
    sendEcommerce('add_payment_info', currency, items, { ...extra, payment_type: paymentType }, { name: 'AddPaymentInfo' });
  }
}

export interface PurchaseDetails {
  transaction_id: string;
  value: number;
  currency: string;
  tax?: number;
  shipping?: number;
  coupon?: string;
  affiliation?: string;
  items: EcommerceItem[];
  // Matches the server-side Conversions API event so Meta deduplicates the two.
  event_id?: string;
}

export interface PurchaseCustomer {
  email?: string | null;
  phone?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  street?: string | null;
  city?: string | null;
  postal_code?: string | null;
  country?: string | null;
}

/** E.164 as Google requires for enhanced conversions; null when the number can't be normalised. */
export function toE164(phone: string | null | undefined, country?: string | null): string | null {
  if (!phone) return null;
  let digits = phone.replace(/[^\d+]/g, '');
  if (digits.startsWith('00')) digits = `+${digits.slice(2)}`;
  if (digits.startsWith('+')) return /^\+\d{8,15}$/.test(digits) ? digits : null;
  const callingCodes: Record<string, string> = { PL: '48', DE: '49', NL: '31', CZ: '420', SK: '421', FR: '33', ES: '34', IT: '39', GB: '44', AT: '43', BE: '32', LT: '370', LV: '371', EE: '372', IE: '353', PT: '351' };
  const code = callingCodes[(country || 'PL').toUpperCase()];
  if (!code) return null;
  digits = digits.replace(/^0+/, '');
  if (digits.startsWith(code) && digits.length > code.length + 6) return `+${digits}`;
  return /^\d{6,13}$/.test(digits) ? `+${code}${digits}` : null;
}

/** Google tag user_data (enhanced conversions) — gtag normalises and SHA-256 hashes it before sending. */
export function toGoogleUserData(customer: PurchaseCustomer | null | undefined) {
  if (!customer) return null;
  const email = customer.email?.trim().toLowerCase() || null;
  const phone = toE164(customer.phone, customer.country);
  const address: Record<string, string> = {};
  if (customer.first_name?.trim()) address.first_name = customer.first_name.trim();
  if (customer.last_name?.trim()) address.last_name = customer.last_name.trim();
  if (customer.street?.trim()) address.street = customer.street.trim();
  if (customer.city?.trim()) address.city = customer.city.trim();
  if (customer.postal_code?.trim()) address.postal_code = customer.postal_code.trim();
  if (customer.country?.trim()) address.country = customer.country.trim().toUpperCase();
  const hasAddress = !!(address.first_name && address.last_name && address.postal_code && address.country);
  if (!email && !phone && !hasAddress) return null;
  return {
    ...(email ? { email } : {}),
    ...(phone ? { phone_number: phone } : {}),
    ...(hasAddress ? { address } : {}),
  };
}

export function trackPurchase(purchase: PurchaseDetails, customer?: PurchaseCustomer | null) {
  if (typeof window === 'undefined' || !hasTrackingConsent()) return;

  const ecommerce = {
    transaction_id: purchase.transaction_id,
    affiliation: purchase.affiliation || 'Online Store',
    value: purchase.value,
    currency: purchase.currency,
    tax: purchase.tax || 0,
    shipping: purchase.shipping || 0,
    coupon: purchase.coupon || '',
    items: purchase.items,
  };

  sendPixel('Purchase', {
    value: purchase.value,
    currency: purchase.currency,
    content_type: 'product',
    content_ids: purchase.items.map((i) => i.item_id),
    num_items: purchase.items.reduce((sum, i) => sum + i.quantity, 0),
  }, purchase.event_id ? { eventID: purchase.event_id } : undefined);

  // Google Tag Manager: a GA4/Ads tag in the container triggers on this event.
  pushDataLayer({ event: 'purchase', ecommerce });

  // GA4 / Google Ads configured directly (Integracje → Google).
  onTrackerReady('gtag', ({ ga4Id, adsId, adsConversion, merchantId }) => {
    try {
      const userData = toGoogleUserData(customer);
      if (userData) window.gtag?.('set', 'user_data', userData);

      const destinations = [ga4Id, adsId].filter(Boolean);
      if (destinations.length > 0) {
        // GA4 purchase + Ads dynamic remarketing "purchase" (items with id/google_business_vertical).
        window.gtag?.('event', 'purchase', { ...ecommerce, items: toGtagItems(purchase.items), send_to: destinations });
      }
      if (adsConversion) {
        window.gtag?.('event', 'conversion', {
          send_to: adsConversion,
          value: purchase.value,
          currency: purchase.currency,
          transaction_id: purchase.transaction_id,
          // Conversions with cart data: lets Ads report which feed items sold.
          ...(merchantId ? {
            aw_merchant_id: merchantId,
            // The product feed is published for PL (g:shipping country) — keep in step with seo.py.
            aw_feed_country: 'PL',
            aw_feed_language: (document.documentElement.lang || 'pl').split('-')[0],
            items: purchase.items.map((i) => ({ id: i.item_id, quantity: i.quantity, price: i.price })),
          } : {}),
        });
      }
    } catch (e) {
      console.error('Failed to send purchase via gtag:', e);
    }
  });
}

export function trackClick(type: 'mailto' | 'tel' | 'link', detail: string, label?: string) {
  if (typeof window === 'undefined' || !hasTrackingConsent()) return;

  pushDataLayer({
    event: 'contact_click',
    contact_click_type: type,
    contact_click_detail: detail,
    contact_click_label: label,
  });
  // Plain button links stay GTM-only; phone/e-mail clicks are real contact intents for GA4/Ads/Meta.
  if (type !== 'link') {
    sendGtag('contact_click', { contact_click_type: type, contact_click_detail: detail, contact_click_label: label });
    sendPixel('Contact', { content_name: label || detail });
  }
}
